import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useState } from 'react';
import { useTranslate, useNotify } from 'react-admin';

import { API_HANDLERS } from '@/api';

interface SendPushModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function SendPushModal({ open, onClose, onSuccess }: SendPushModalProps): JSX.Element {
  const translate = useTranslate();
  const notify = useNotify();
  const [pushTitle, setPushTitle] = useState('');
  const [pushText, setPushText] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!pushTitle || pushTitle.length < 3 || pushTitle.length > 150) {
      newErrors.pushTitle = translate('pos.communication.errors.title_length');
    }

    if (!pushText || pushText.length < 3 || pushText.length > 250) {
      newErrors.pushText = translate('pos.communication.errors.text_length');
    }

    if (note && (note.length < 3 || note.length > 150)) {
      newErrors.note = translate('pos.communication.errors.note_length');
    }

    if (!file) {
      newErrors.file = translate('pos.communication.errors.file_required');
    } else {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(fileExtension || '')) {
        newErrors.file = translate('pos.communication.errors.file_format');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      await API_HANDLERS.COMMUNICATION.SEND_PUSH({
        pushTitle,
        pushText,
        note: note || undefined,
        file: file!,
      });

      notify(translate('pos.communication.success.sent'), { type: 'success' });
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('Error sending push:', error);

      // Extract error messages from backend response
      const errorMessages = error?.response?.data?.message;

      if (typeof errorMessages === 'string') {
        // Single error message as string
        notify(errorMessages, { type: 'error' });
      } else if (Array.isArray(errorMessages) && errorMessages.length > 0) {
        // Multiple error messages as array
        const combinedErrors = errorMessages.join('\n');
        notify(combinedErrors, { type: 'error', multiLine: true });
      } else {
        // Fallback to generic error message
        notify(translate('pos.communication.errors.send_failed'), { type: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPushTitle('');
      setPushText('');
      setNote('');
      setFile(null);
      setErrors({});
      onClose();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {translate('pos.communication.modal_title')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label={translate('pos.communication.fields.title')}
              value={pushTitle}
              onChange={e => setPushTitle(e.target.value)}
              error={!!errors.pushTitle}
              helperText={errors.pushTitle || `${pushTitle.length}/150`}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 150 }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label={translate('pos.communication.fields.text')}
              value={pushText}
              onChange={e => setPushText(e.target.value)}
              error={!!errors.pushText}
              helperText={errors.pushText || `${pushText.length}/250`}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 250 }}
            />

            <TextField
              fullWidth
              label={translate('pos.communication.fields.note')}
              value={note}
              onChange={e => setNote(e.target.value)}
              error={!!errors.note}
              helperText={errors.note || translate('pos.communication.fields.note_optional')}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 150 }}
            />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                {translate('pos.communication.fields.recipients')}
              </Typography>
              <Button variant="outlined" component="label" fullWidth>
                {file ? file.name : translate('pos.communication.fields.choose_file')}
                <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFileChange} />
              </Button>
              {errors.file && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {errors.file}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {translate('pos.communication.fields.file_format')}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              width: 280,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              backgroundColor: '#f5f5f5',
              borderRadius: 3,
              p: 3,
              pt: 2,
            }}
          >
            <Typography variant="body2" sx={{ mb: 2, fontSize: 13, fontWeight: 500 }}>
              {translate('pos.communication.preview.header')}
            </Typography>

            <Box
              sx={{
                width: 200,
                height: 400,
                backgroundColor: '#1a1a1a',
                borderRadius: '30px',
                border: '8px solid #2a2a2a',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Phone notch */}
              <Box
                sx={{
                  width: 60,
                  height: 20,
                  backgroundColor: '#1a1a1a',
                  borderBottomLeftRadius: '10px',
                  borderBottomRightRadius: '10px',
                  position: 'absolute',
                  top: 0,
                  zIndex: 2,
                }}
              />

              {/* Phone screen */}
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#e8f4f8',
                  mt: '20px',
                  pt: 1,
                  px: 2,
                }}
              >
                {/* Status bar with time */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#1a1a1a' }}>10:18</Typography>
                </Box>

                {/* Notification card */}
                <Box
                  sx={{
                    backgroundColor: 'white',
                    borderRadius: 2,
                    p: 1.5,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  {/* App icon */}
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: '#7c3aed',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ color: 'white', fontSize: 18, fontWeight: 700 }}>F</Typography>
                  </Box>

                  {/* Notification content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {pushTitle || translate('pos.communication.preview.title_placeholder')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 9,
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {pushText || translate('pos.communication.preview.text_placeholder')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Phone home indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  width: 120,
                  height: 4,
                  backgroundColor: '#666',
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {translate('pos.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            backgroundColor: '#7c3aed',
            '&:hover': { backgroundColor: '#6d28d9' },
          }}
        >
          {loading ? <CircularProgress size={24} /> : translate('pos.communication.send_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SendPushModal;
